import { TemplateWrapper } from './Wrapper';
import { Controller } from 'react-hook-form';
import { Textarea } from '@design-system/components/textarea/textarea';
import { MenuItem, Select, Slider } from '@mui/material';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { faPlus, faClose } from '@fortawesome/pro-regular-svg-icons';
import _ from 'lodash';
import { InputNumber } from '@design-system/components/input/inputNumber';
import { bgColorsVars } from '@design-system/vars.css';
import { EditWorkoutVM } from '../../view-models/workoutEdit.viewModel';
import { WorkoutItemTemplates } from '../../view-models/mappers';
import { ArrayItem, TypedOmit } from '@client/new/helpers/types';
import {
  LegacyExercise as ExerciseType,
  Set,
} from '../../models/workout.domain';
import { InfoAlert } from '@client/new/components/InfoAlert';
import { IconButton } from '@design-system/components/button/iconButton';
import { TitleInputWithHints } from '../../components/titleInputWithHints/TitleInputWithHints';
import { Hint, HintType } from '../../components/Hint';
import { MediaPreview } from '../../components/MediaPreview';
import { clsx } from 'clsx';

type ExerciseProps = {
  index: number;
  circuitIndex?: number;
  expand: boolean;
} & Pick<
  EditWorkoutVM,
  | 'influencerId'
  | 'dispatch'
  | 'control'
  | 'submitting'
  | 'inputHints'
  | 'readonly'
  | 'exerciseSuggestions'
> &
  Pick<ArrayItem<WorkoutItemTemplates>, 'header' | 'menuItems'> &
  TypedOmit<ExerciseType, 'id'> & { id?: string };

export const ExerciseMedia = (p: ExerciseProps) => {
  return (
    <div>
      <MediaPreview media={p.media} videoType="short" />
    </div>
  );
};

export const Exercise = (p: ExerciseProps) => {
  const showRestBetween = (p.sets || []).length > 1;
  return (
    <div>
      <TemplateWrapper
        {...p}
        isCircuit={p.circuitIndex !== undefined && p.circuitIndex >= 0}
      >
        <div className="bg-white w-full flex flex-col md:flex-row p-4 gap-6 rounded-b-xl shadow-exercise">
          <div className="w-[244px]">
            {p.media ? (
              <MediaPreview
                media={p.media}
                videoType="short"
                removeVideo={() =>
                  p.dispatch('removeMedia', {
                    index: p.index,
                    circuitIndex: p.circuitIndex,
                  })
                }
                disabled={p.readonly}
              />
            ) : (
              <div className="w-[244px] h-[220px]">
                <Button
                  color="neutral"
                  onClick={() =>
                    p.dispatch('openLibrary', {
                      index: p.index,
                      circuitIndex: p.circuitIndex,
                      type: 'short',
                      action: 'update',
                    })
                  }
                  leftIcon={faPlus}
                  disabled={p.readonly}
                >
                  Add
                </Button>
              </div>
            )}
          </div>
          <div className="w-full flex flex-col gap-2">
            <Controller
              name={
                p?.circuitIndex !== undefined
                  ? `exercises.${p.circuitIndex}.exercises.${p.index}.name`
                  : `exercises.${p.index}.name`
              }
              control={p.control}
              render={({ field: { onChange, value }, fieldState }) => (
                <TitleInputWithHints
                  required={true}
                  value={value}
                  onChange={onChange}
                  onBlur={() => {
                    p.dispatch('clearRecommendations');
                  }}
                  disabled={p.submitting || p.readonly}
                  error={fieldState.error?.message}
                  exerciseSuggestions={p.exerciseSuggestions}
                  onUpdate={(description: string, uuid: string) =>
                    p.dispatch('exerciseTitleUpdate', {
                      circuitIndex: p.circuitIndex,
                      index: p.index,
                      description,
                      uuid,
                    })
                  }
                />
              )}
            />
            <Controller
              name={
                p?.circuitIndex !== undefined
                  ? `exercises.${p.circuitIndex}.exercises.${p.index}.description`
                  : `exercises.${p.index}.description`
              }
              control={p.control}
              render={({ field: { onChange, value }, fieldState }) => (
                <Textarea
                  label="Description"
                  rows={4}
                  disabled={p.submitting || p.readonly}
                  value={value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
            <InfoAlert text="Changes here won't update the title or description in the Library. Update the file in Library for changes." />
            <Hint
              hint={p.inputHints?.[p.uuid || '']}
              onUpdate={(hint: HintType) =>
                p.dispatch('applyHint', {
                  circuitIndex: p.circuitIndex,
                  index: p.index,
                  hint,
                  uuid: p.uuid || '',
                })
              }
            />
          </div>
          <div className="w-full flex flex-col pt-[28px]">
            <Sets
              sets={p.sets || []}
              index={p.index}
              dispatch={p.dispatch}
              submitting={p.submitting}
              circuitIndex={p.circuitIndex}
              readonly={p.readonly}
              control={p.control}
            />
            {showRestBetween ? (
              <RestBetween
                index={p.index}
                control={p.control}
                circuitIndex={p.circuitIndex}
                disabled={p.readonly}
              />
            ) : null}
          </div>
        </div>
      </TemplateWrapper>
    </div>
  );
};
export const RestBetween = ({
  index,
  control,
  circuitIndex,
  disabled,
}: {
  index: number;
  control: EditWorkoutVM['control'];
  circuitIndex?: number;
  disabled?: boolean;
}) => (
  <Controller
    name={
      circuitIndex !== undefined
        ? `exercises.${circuitIndex}.exercises.${index}.rest`
        : `exercises.${index}.rest`
    }
    control={control}
    render={({ field: { onChange, value } }) => (
      <div className="flex flex-col pt-6">
        <div className="flex justify-between items-center">
          <Typography>Rest between</Typography>
          <Typography>{`${value || 0} sec`}</Typography>
        </div>
        <div className="mx-2">
          <Slider
            value={value}
            onChange={(event: Event, newValue: number | number[]) =>
              onChange(newValue as number)
            }
            min={0}
            max={300}
            sx={{
              '&.MuiSlider-root  > .MuiSlider-track': {
                color: bgColorsVars['P-80'],
              },
              '&.MuiSlider-root  > .MuiSlider-thumb': {
                color: bgColorsVars['P-80'],
              },
            }}
            disabled={disabled}
          />
        </div>
      </div>
    )}
  />
);

const setUnits = [
  { label: 'reps', value: 'reps' },
  { label: 'sec', value: 'sec' },
  { label: 'min', value: 'min' },
  { label: 'meters', value: 'meters' },
  { label: 'calories', value: 'calories' },
];
const durationUnits = [
  { label: 'rounds', value: 'rounds' },
  { label: 'sec', value: 'sec' },
  { label: 'min', value: 'min' },
  { label: 'meters', value: 'meters' },
  { label: 'calories', value: 'calories' },
];

export const Duration = ({
  control,
  index,
  submitting,
  circuitIndex,
  readonly,
}: Pick<EditWorkoutVM, 'control' | 'submitting' | 'readonly'> & {
  index: number;
  circuitIndex?: number;
}) => {
  return (
    <Controller
      name={
        circuitIndex !== undefined
          ? `exercises.${circuitIndex}.exercises.${index}.duration`
          : `exercises.${index}.duration`
      }
      control={control}
      render={({ field: { value, onChange }, fieldState }) => {
        return (
          <div className="flex items-end gap-2">
            <InputNumber
              label="Duration"
              disabled={submitting || readonly}
              value={value[0]}
              onChange={(newValue) => onChange([newValue, value[1]])}
              error={fieldState.error?.message}
              min={0}
            />
            <Select
              value={value[1]}
              defaultValue={'min'}
              sx={{
                minWidth: '130px',
                height: 44,
                padding: '8px 16px',
                border: 'solid 2px',
                borderRadius: '10px',
                borderColor: bgColorsVars['N-40'],
                backgroundColor: bgColorsVars['W-100'],
                boxShadow: 'none',
                fontWeight: 500,
                fontFamily: 'inherit',
                margin: '1px',
                '&.Mui-focused': {
                  margnin: 0,
                  boxShadow: 'none',
                  borderWidth: '3px',
                },
              }}
              disabled={readonly}
            >
              {durationUnits.map((option) => (
                <MenuItem
                  key={option.label}
                  value={option.value}
                  onClick={() => onChange([value[0], option.value])}
                >
                  <Typography variant="body" size="large">
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      }}
    />
  );
};

const Sets = ({
  sets,
  index,
  dispatch,
  submitting,
  circuitIndex,
  readonly,
  control,
}: Pick<EditWorkoutVM, 'dispatch' | 'submitting' | 'readonly' | 'control'> & {
  index: number;
  circuitIndex?: number;
  sets: Set[];
}) => {
  const showDuration = sets.length === 1;
  return (
    <div className="flex flex-col gap-2" data-testid="sets" key={index}>
      {sets.map(({ reps }: Set, i: number) => (
        <Controller
          name={
            circuitIndex !== undefined
              ? `exercises.${circuitIndex}.exercises.${index}.sets.${i}.reps`
              : `exercises.${index}.sets.${i}.reps`
          }
          key={i}
          control={control}
          render={({ field: { onChange }, fieldState: { error } }) => (
            <div
              className={clsx(
                'flex gap-2 h-max',
                error && 'items-start',
                !error && 'items-center',
              )}
              key={i}
            >
              <div className="flex items-center gap-2">
                <Typography className="w-[60px]">
                  {showDuration ? 'Duration' : `Set ${i + 1}`}
                </Typography>

                <InputNumber
                  disabled={submitting || readonly}
                  value={reps[0]}
                  onChange={(val) => onChange([val, reps[1]])}
                  error={error && error?.message}
                  min={0}
                />
              </div>

              <Select
                value={reps[1]}
                className="w-[120px]"
                defaultValue={'min'}
                sx={{
                  minWidth: '130px',
                  height: 44,
                  padding: '8px 16px',
                  border: 'solid 2px',
                  borderRadius: '10px',
                  borderColor: bgColorsVars['N-40'],
                  backgroundColor: bgColorsVars['W-100'],
                  boxShadow: 'none',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  margin: '1px',
                  '&.Mui-focused': {
                    margnin: 0,
                    boxShadow: 'none',
                    borderWidth: '3px',
                  },
                }}
                disabled={readonly}
              >
                {setUnits.map((option) => (
                  <MenuItem
                    key={option.label}
                    value={option.value}
                    onClick={() => onChange([reps[0], option.value])}
                  >
                    <Typography variant="body" size="large">
                      {option.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              {i !== 0 && (
                <IconButton
                  variant="text"
                  color="neutral"
                  icon={faClose}
                  iconClassName="font-normal text-[20px]"
                  disabled={readonly}
                  onClick={() =>
                    circuitIndex !== undefined && circuitIndex >= 0
                      ? dispatch('removeCircuitSet', {
                          circuitIndex,
                          exercise: index,
                          set: i,
                        })
                      : dispatch('removeSet', { exercise: index, set: i })
                  }
                />
              )}
            </div>
          )}
        />
      ))}
      <Button
        color="primaryTonal"
        leftIcon={faPlus}
        className="w-[83px] mt-2"
        onClick={() =>
          circuitIndex !== undefined && circuitIndex >= 0
            ? dispatch('addCircuitSet', { circuitIndex, index })
            : dispatch('addSet', index)
        }
        disabled={readonly}
      >
        Set
      </Button>
    </div>
  );
};
